// SpaceContentListPage.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import SpaceContentDetailModal from "./modal/SpaceContentDetailModal";
import SpaceContentEditModal from "./modal/SpaceContentEditModal";

export function SpaceContentListPage() {
    const [data, setData] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [locationTypeFilter, setLocationTypeFilter] = useState("");
    const [activeThemes, setActiveThemes] = useState([]);
    const [activeKeywords, setActiveKeywords] = useState([]);
    const [eventNameFilter, setEventNameFilter] = useState("");
    const [locationFilter, setLocationFilter] = useState("");
    const [allThemes, setAllThemes] = useState([]);
    const [allKeywords, setAllKeywords] = useState([]);
    const [allEventNames, setAllEventNames] = useState([]);
    const [allLocations, setAllLocations] = useState([]);
    const [detailView, setDetailView] = useState(null);
    const [editingContent, setEditingContent] = useState(null);

    useEffect(() => {
        axios
            .get(`${process.env.REACT_APP_API_BASE_URL}/space-content/list`)
            .then((res) => {
                setData(res.data);
                setFiltered(res.data);

                const themes = new Set(),
                    keywords = new Set(),
                    names = new Set(),
                    locations = new Set();

                res.data.forEach((d) => {
                    d.themes.forEach((t) => themes.add(t));
                    d.keywords.forEach((k) => keywords.add(k));
                    names.add(d.eventName);
                    if (d.location) locations.add(d.location);
                });

                setAllThemes([...themes]);
                setAllKeywords([...keywords]);
                setAllEventNames([...names]);
                setAllLocations([...locations]);
            });
    }, []);

    useEffect(() => {
        const filteredData = data.filter((d) => {
            const matchLocationType = locationTypeFilter
                ? d.locationType === locationTypeFilter
                : false;
            const matchTheme = activeThemes.length
                ? activeThemes.some((t) => d.themes.includes(t))
                : false;
            const matchKeyword = activeKeywords.length
                ? activeKeywords.some((k) => d.keywords.includes(k))
                : false;
            const matchEvent = eventNameFilter
                ? d.eventName.includes(eventNameFilter)
                : false;
            const matchLocation = locationFilter
                ? d.location?.includes(locationFilter)
                : false;

            const isAnyFilterSet =
                locationTypeFilter ||
                activeThemes.length ||
                activeKeywords.length ||
                eventNameFilter ||
                locationFilter;

            return isAnyFilterSet
                ? matchLocationType || matchTheme || matchKeyword || matchEvent || matchLocation
                : true;
        });
        setFiltered(filteredData);
    }, [
        locationTypeFilter,
        activeThemes,
        activeKeywords,
        eventNameFilter,
        locationFilter,
        data,
    ]);

    // 필터 초기화 함수
    const resetFilters = () => {
        setLocationTypeFilter("");
        setActiveThemes([]);
        setActiveKeywords([]);
        setEventNameFilter("");
        setLocationFilter("");
    };

    const toggle = (value, list, setList) => {
        setList((prev) =>
            prev.includes(value)
                ? prev.filter((v) => v !== value)
                : [...prev, value]
        );
    };

    const handleDelete = async (id) => {
        if (!window.confirm("정말 삭제하시겠습니까?")) return;
        await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/space-content/${id}`);
        setData((prev) => prev.filter((d) => d.id !== id));
    };

    const handleUpdate = (updatedItem) => {
        setData((prev) => prev.map((d) => (d.id === updatedItem.id ? updatedItem : d)));
        setEditingContent(null);
    };

    const handleDownload = async (eventName, location) => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/space-content/download-zip`,
                {
                    params: { event: eventName, location },
                    responseType: "blob",
                }
            );
            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `${eventName}_${location}.zip`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            alert("다운로드 실패");
        }
    };

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <h2 className="text-2xl font-extrabold mb-6">3D 목록</h2>

            {/* 검색/필터 영역 */}
            <div className="bg-white border rounded-lg p-4 mb-6 shadow-sm">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    {/* 실내/실외 필터 */}
                    <div>
                        <label className="block text-sm font-medium mb-1">실내/실외</label>
                        <select
                            value={locationTypeFilter}
                            onChange={(e) => setLocationTypeFilter(e.target.value)}
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">전체</option>
                            <option value="실내">실내</option>
                            <option value="실외">실외</option>
                        </select>
                    </div>
                    {/* 행사명 필터 */}
                    <div>
                        <label className="block text-sm font-medium mb-1">행사명</label>
                        <input
                            value={eventNameFilter}
                            onChange={(e) => setEventNameFilter(e.target.value)}
                            list="eventNameList"
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="행사명 입력"
                        />
                        <datalist id="eventNameList">
                            {allEventNames.map((n, i) => (
                                <option key={i} value={n} />
                            ))}
                        </datalist>
                    </div>
                    {/* 장소명 필터 */}
                    <div>
                        <label className="block text-sm font-medium mb-1">장소명</label>
                        <input
                            value={locationFilter}
                            onChange={(e) => setLocationFilter(e.target.value)}
                            list="locationList"
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="장소명 입력"
                        />
                        <datalist id="locationList">
                            {allLocations.map((loc, i) => (
                                <option key={i} value={loc} />
                            ))}
                        </datalist>
                    </div>

                </div>

                {/* 행사 성격(테마) 토글 */}
                <div className="mb-4">
                    <label className="block text-sm font-semibold mb-2">행사 성격</label>
                    <div className="flex flex-wrap gap-2">
                        {allThemes.map((t, i) => (
                            <button
                                key={i}
                                onClick={() => toggle(t, activeThemes, setActiveThemes)}
                                className={`px-3 py-1 rounded-full border text-xs transition ${
                                    activeThemes.includes(t)
                                        ? "bg-green-600 text-white border-green-600"
                                        : "bg-gray-100 text-gray-800 hover:bg-gray-200 border-gray-200"
                                }`}
                            >
                                {t}
                            </button>
                        ))}
                    </div>
                </div>

                {/* 키워드 토글 */}
                <div>
                    <label className="block text-sm font-semibold mb-2">키워드</label>
                    <div className="flex flex-wrap gap-2">
                        {allKeywords.map((k, i) => (
                            <button
                                key={i}
                                onClick={() => toggle(k, activeKeywords, setActiveKeywords)}
                                className={`px-3 py-1 rounded-full border text-xs transition ${
                                    activeKeywords.includes(k)
                                        ? "bg-blue-600 text-white border-blue-600"
                                        : "bg-gray-100 text-gray-800 hover:bg-gray-200 border-gray-200"
                                }`}
                            >
                                {k}
                            </button>
                        ))}
                    </div>

                    {/* 오른쪽 하단 정렬 */}
                    <div className="flex justify-end">
                        <button
                            className="px-4 py-2 rounded bg-red-400 text-white shadow-sm hover:bg-rose-500 transition"
                            onClick={resetFilters}
                        >
                            필터 초기화
                        </button>
                    </div>

                </div>

            </div>




            {/* 카드 목록을 grid로 배치 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-2 max-w-7xl">
                {filtered.map((d) => (
                    <div
                        key={d.id}
                        className="group relative border rounded-lg bg-white shadow-sm hover:shadow-md
        overflow-hidden transition-shadow cursor-pointer"
                        onClick={() =>
                            setDetailView({ eventName: d.eventName, location: d.location })
                        }
                    >
                        {/* 이미지 영역 */}
                        {d.filePaths.length > 0 && (
                            <div className="relative overflow-hidden h-64 min-h-[400px]">
                                <img
                                    src={`${process.env.REACT_APP_API_REAL_URL}/${d.thumbnailFilePath}`}
                                    alt="썸네일"
                                    className="object-cover w-full h-full"
                                />
                            </div>
                        )}

                        {/* 카드 하단 정보/버튼 영역 */}
                        <div className="p-3">
                            <div
                                className="flex justify-end gap-3 text-sm mt-2"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <button
                                    onClick={() => setEditingContent(d)}
                                    className="text-green-600 hover:text-green-800 transition"
                                >
                                    수정
                                </button>
                                <button
                                    onClick={() => handleDelete(d.id)}
                                    className="text-red-600 hover:text-red-800 transition"
                                >
                                    삭제
                                </button>
                                <button
                                    onClick={() => handleDownload(d.eventName, d.location)}
                                    className="text-blue-600 hover:text-blue-800 transition"
                                >
                                    다운로드
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* 상세 보기 모달 */}
            {detailView && (
                <SpaceContentDetailModal
                    eventName={detailView.eventName}
                    location={detailView.location}
                    onClose={() => setDetailView(null)}
                />
            )}

            {/* 수정 모달 */}
            {editingContent && (
                <SpaceContentEditModal
                    item={editingContent}
                    onClose={() => setEditingContent(null)}
                    onSave={handleUpdate}
                />
            )}
        </div>
    );
}